import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, collisionPadding = 8, ...props }, ref) => (
  // ── Why this is wrapped in TooltipPrimitive.Portal ─────────────────────
  // Radix's `Tooltip.Content` renders INLINE in the DOM tree by default
  // (unlike Popover / Dialog / DropdownMenu, which auto-portal). When the
  // tooltip's trigger sits inside an `overflow:hidden` ancestor — which
  // every playlist `<Card>` has, plus our maximized GlobalSpotifyPlayer
  // and several modals — the tooltip is rendered as a child of that
  // ancestor and gets clipped at the boundary.
  //
  // Symptom observed 2026-05-12 + 2026-05-21: leftmost-card Refine button
  // showed tooltip as "efine playlist" (leading "R" clipped), Remix as
  // "emix · same vibe, new tracks". `collisionPadding={8}` was the prior
  // fix attempt — that only handles VIEWPORT edge collisions, not ancestor
  // overflow clipping. Wrapping in Portal escapes all ancestor stacking +
  // overflow contexts and renders the tooltip at <body> level where only
  // the viewport bounds matter (which collisionPadding already handles).
  //
  // collisionPadding stays at 8 (Radix default = 0) so the now-portaled
  // tooltip still slides inward at the viewport edge instead of clipping
  // off-screen.
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      className={cn(
        "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
